import { MobileDatePicker } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import produce from 'immer';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { WrappedDocumentReference } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import {
  PractitionerWrapper,
  WrappedPractitioner,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import {
  useDocumentReference,
  useOrganizations,
  usePractitioners,
  useValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import CancelButton from 'src/careflow/common/CancelButton';
import { convertValueToValueSet } from 'src/careflow/common/common-utils';

type FormValue = {
  name: string;
  category: string[];
  author: string[];
  custodian: string;
  type: string;
  date: string;
};

type Props = {
  document: WrappedDocumentReference | null;
  open: boolean;
  onClose: VoidFunction;
  wrappedPractionersIndex?: {};
};

export default function DocumentEditForm({
  document,
  open,
  onClose,
  wrappedPractionersIndex,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();

  const [organizations] = useOrganizations({ map: OrganizationWrapper });

  const [practitioner] = usePractitioners({ map: PractitionerWrapper });

  const [documentType] = useValueSet('ph-document-type-codes', { map: ValueSetWrapper });

  const [documentCategory] = useValueSet('ph-document-category-codes', { map: ValueSetWrapper });

  const [, { update }] = useDocumentReference(document?.id!);

  const defaultValues = useMemo(
    () =>
      ({
        name: document?.content?.[0].attachment.title,
        category: document?.category?.map((e) => (e.text ? e.text : e.coding?.[0].display)),
        author: document?.author?.map((e) => {
          if (e.display) {
            return e.display;
          } else if (
            wrappedPractionersIndex &&
            e.reference &&
            e.reference.includes('Practitioner/')
          ) {
            const splitRef = e.reference.split('/');
            const practitionerId = splitRef[splitRef.length - 1];
            const wPractitioner = wrappedPractionersIndex[practitionerId] as WrappedPractitioner;

            if (wPractitioner) {
              const fullName = wPractitioner.getFullName();

              if (fullName) {
                return fullName;
              }
            }
          }

          return '';
        }),
        custodian: document?.custodian?.display,
        type: document?.type?.coding?.[0].display,
        date: document?.date,
      } as FormValue),
    [document, wrappedPractionersIndex]
  );

  const methods = useForm({ defaultValues });

  const { reset, control, handleSubmit } = methods;

  const onSubmit = async (data: FormValue) => {
    try {
      const newAuthor = data.author?.map((e) => {
        let author: any = practitioner
          ?.filter((r) => r.getFullName() === e)
          .map((r) => ({
            display: r.getFullName(),
            reference: `Practitioner/${r.id}`,
          }))[0];

        if (!author) {
          author = { display: e };
        }

        return author;
      });

      const codeValueCategory =
        data.category?.map((e) => {
          let valueSet = convertValueToValueSet(e, documentCategory);

          if (!valueSet) {
            valueSet = { code: e, display: e };
          }

          return {
            coding: [{ ...valueSet }],
            text: e,
          };
        }) || [];

      const newCustodian = organizations
        ?.filter((e) => e.name === data.custodian)
        .map((t) => {
          const reference = `Organization/${t.id}`;
          const display = t.name;
          return { reference, display };
        });

      const codeValueType = convertValueToValueSet(data.type, documentType);

      await update(
        produce(document!, (draft) => {
          draft.content[0].attachment.title = data.name;
          draft.category = [...codeValueCategory];
          draft.author = newAuthor;
          draft.custodian = newCustodian[0];
          draft.type = { coding: [{ ...codeValueType }] };
          draft.date = data.date;
        })
      );

      enqueueSnackbar('Document was updated.');
      onClose();
    } catch (e) {
      enqueueSnackbar('Document was not updated.', { variant: 'error' });
      onClose();
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [document]);

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{i18n('patients.details.attachments.documentsDetails', 'crs')}</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <Typography variant="body2">{i18n('patients.details.attachments.name', 'crs')}</Typography>
                <RHFTextField name="name" label="" />

                <Typography variant="body2">{i18n('patients.details.attachments.category', 'crs')}</Typography>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      freeSolo
                      onChange={(event, newValue) => field.onChange(newValue)}
                      options={documentCategory?.asList().map((option) => option.display) || []}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            size="small"
                            label={option}
                          />
                        ))
                      }
                      renderInput={(params) => <TextField label="" {...params} />}
                    />
                  )}
                />

                <Typography variant="body2">{i18n('patients.details.attachments.author', 'crs')}</Typography>
                <Controller
                  name="author"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      freeSolo
                      onChange={(event, newValue) => field.onChange(newValue)}
                      options={practitioner?.map((option) => option?.getFullName()) || []}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            size="small"
                            label={option}
                          />
                        ))
                      }
                      renderInput={(params) => <TextField label="" {...params} />}
                    />
                  )}
                />

                <Typography variant="body2">{i18n('patients.details.attachments.custodian', 'crs')}</Typography>
                <RHFSelect name="custodian" label="">
                  {organizations?.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <Typography variant="body2">{i18n('patients.details.attachments.type', 'crs')}</Typography>
                <RHFSelect name="type" label="">
                  {documentType?.asList().map((option) => (
                    <MenuItem key={option.code} value={option.display}>
                      {option.display}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <Typography variant="body2">{i18n('patients.details.attachments.date', 'crs')}</Typography>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <MobileDatePicker
                      {...field}
                      label=""
                      inputFormat="MMM dd, yyyy hh:mm"
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  )}
                />
              </Stack>
            </Grid>
          </Grid>
          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />

              <CancelButton onClick={handleClose}>
                {i18n('cancel')}
              </CancelButton>

              <Button variant="contained" type="submit">
                {i18n('saveChanges')}
              </Button>
            </DialogActions>
          </Stack>
        </Card>
      </FormProvider>
    </Dialog>
  );
}
